import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "./utils";
import {
  QuestionnaireSectionsForNavigation,
  QuestionGroupWithRelations,
  RestrictedQuestionOptionVisibility,
  QuestionnaireSectionWithStatus,
} from "../../../api/src/types";

export const createQuestionnaireSectionsTagId = (params: {
  userId: string;
  includeStatus: boolean;
}): string => {
  const statusKey = params.includeStatus ? "with-status" : "no-status";
  return `questionnaire-sections:${params.userId}:${statusKey}`;
};

export const createQuestionnaireSectionDetailsTagId = (params: {
  userId: string;
  section: string;
}): string => {
  return `questionnaire-section:${params.userId}:${params.section}`;
};

export const questionnaireApi = createApi({
  reducerPath: "questionnaireApi",
  baseQuery,
  tagTypes: [
    "QuestionnaireSections",
    "QuestionGroup",
    "QuestionOptionVisibility",
  ],
  endpoints: (builder) => ({
    getQuestionnaireSections: builder.query<
      QuestionnaireSectionsForNavigation[],
      { userId: string; includeStatus: boolean; documentId: string }
    >({
      query: ({ userId, includeStatus, documentId }) => ({
        url: `users/${userId}/documents/${documentId}/questionnaire-sections`,
        method: "GET",
        params: { includeStatus },
      }),
      providesTags: (_result, _error, args) => [
        {
          type: "QuestionnaireSections" as const,
          id: createQuestionnaireSectionsTagId({
            userId: args.userId,
            includeStatus: args.includeStatus,
          }),
        },
      ],
    }),
    getQuestionnaireSectionDetails: builder.query<
      QuestionnaireSectionWithStatus,
      { documentId: string; userId: string; section: string }
    >({
      query: ({ documentId, userId, section }) => ({
        url: `users/${userId}/documents/${documentId}/questionnaire-sections/${section}`,
        method: "GET",
      }),
      providesTags: (_result, _error, args) => [
        {
          type: "QuestionnaireSections" as const,
          id: createQuestionnaireSectionDetailsTagId({
            userId: args.userId,
            section: args.section,
          }),
        },
      ],
    }),
    getQuestionnaireSectionGroupsByDocumentId: builder.query<
      QuestionGroupWithRelations[],
      { docId: string; section: string; userId: string }
    >({
      query: ({ docId, section, userId }) => ({
        url: `users/${userId}/documents/${docId}/questionnaire-sections/${section}/groups`,
        method: "GET",
      }),
      providesTags: [{ type: "QuestionGroup" as const }],
    }),
    getQuestionOptionVisibility: builder.query<
      RestrictedQuestionOptionVisibility,
      {
        restrictedQuestionOptionIds: string[];
        userId: string;
        documentId: string;
      }
    >({
      query: ({ restrictedQuestionOptionIds, userId, documentId }) => ({
        url: `users/${userId}/question-options`,
        method: "POST",
        body: { restrictedQuestionOptionIds },
        params: { documentId },
      }),
      providesTags: ["QuestionOptionVisibility"],
    }),
  }),
});

export const {
  useGetQuestionnaireSectionsQuery,
  useGetQuestionnaireSectionDetailsQuery,
  useGetQuestionnaireSectionGroupsByDocumentIdQuery,
  useGetQuestionOptionVisibilityQuery,
} = questionnaireApi;
